import { IconDownload } from "@tabler/icons-react";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  IconButton,
  ListaItemsSkeleton,
  Loader,
  Text,
} from "@/components/ui";
import { descargarTrabajo, type TrabajoDto } from "../services/reporteria.api";
import { useTrabajos } from "../hooks/use-reporteria";

function estadoBadge(estado: string) {
  const map: Record<string, string> = {
    Completado: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200",
    Error: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200",
    Expirado: "bg-muted text-muted-foreground",
    Procesando: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
    Pendiente: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
  };
  return map[estado] ?? "bg-muted text-muted-foreground";
}

export function MisReportesPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const trabajos = useTrabajos();

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Mis reportes generados</DialogTitle>
        </DialogHeader>
        {trabajos.isLoading ? (
          <ListaItemsSkeleton items={4} />
        ) : !trabajos.data || trabajos.data.items.length === 0 ? (
          <Text size="sm" c="dimmed" className="py-4">
            Aún no has exportado reportes. Arma uno y usa “Exportar a Excel”.
          </Text>
        ) : (
          <div className="max-h-[60vh] space-y-1.5 overflow-y-auto pr-1">
            {trabajos.data.items.map((t: TrabajoDto) => (
              <div key={t.id} className="flex items-center gap-3 rounded-md border border-border p-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium">{t.titulo}</span>
                    <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${estadoBadge(t.estado)}`}>
                      {t.estado}
                    </span>
                    {t.truncado && t.estado === "Completado" && (
                      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                        Truncado
                      </span>
                    )}
                  </div>
                  <Text size="xs" c="dimmed">
                    {new Date(t.fechaCreacion).toLocaleString("es-MX")}
                    {t.totalFilas != null ? ` · ${t.totalFilas.toLocaleString("es-MX")} filas` : ""}
                    {t.mensajeError ? ` · ${t.mensajeError}` : ""}
                  </Text>
                  {t.truncado && t.estado === "Completado" && (
                    <Text size="xs" className="text-amber-700 dark:text-amber-300">
                      Incompleto: el reporte superó el máximo de filas por exportación y el archivo
                      trae solo las primeras {t.totalFilas?.toLocaleString("es-MX") ?? ""} (ver la hoja
                      «Aviso»). Agrega filtros para obtener el resto.
                    </Text>
                  )}
                </div>
                {t.descargable ? (
                  <IconButton aria-label="Descargar" variant="light" onClick={() => descargarTrabajo(t)}>
                    <IconDownload size={16} />
                  </IconButton>
                ) : t.estado === "Procesando" || t.estado === "Pendiente" ? (
                  <Loader size="xs" />
                ) : null}
              </div>
            ))}
          </div>
        )}
        <div className="mt-2 flex justify-end">
          <Button variant="default" onClick={onClose}>
            Cerrar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
